import { FC, useCallback, useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Stack, Text } from '@chakra-ui/react';
import { MetricTooltipContent } from '../MetricTooltipContent';
import moment from 'moment';
import SprintBarChart from './SprintBarChart';
import SprintAreaChart from './AreaChart';
import { fillColors } from '../shared/utils';
import DonutChart from '../DonutChart';
import { DataGrid } from '../GridTable/DataGrid';
import { Paginate } from '../appPaginate';
import VerticalBarChartShared from '../verticalBarChartShared';
import { StyledLink } from '../StyledLink';
import { InfoIcon } from '../icons';
import AppTooltip from '../appTooltip';

interface SprintTableSharedProps {
  data: any;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  loading: boolean;
  team: string;
  slideDrawer: any;
  setBlockedIssues: any;
  maxH?: string;
}

export const SprintTableShared: FC<SprintTableSharedProps> = ({
  data,
  currentPage,
  setCurrentPage,
  loading,
  team,
  slideDrawer,
  setBlockedIssues,
  maxH,
}: SprintTableSharedProps) => {
  const rows: any[] = useMemo(() => data?.reports || [], [data?.reports]);

  const columnHelper = createColumnHelper<any>();

  const columns = useMemo(
    () => [
      columnHelper.accessor('sprintDetail.sprintName', {
        cell: (info) => (
          <Flex direction="column">
            {info.row.original?.sprintDetail?.sprintName && (
              <StyledLink
                path={`/sprint/${info?.row?.original?.sprintDetail?.sprintId}${
                  team ? `?team=${team}` : `?team=${'Organisation'}`
                }`}
                fontSize="16px"
              >
                {info.row.original?.sprintDetail?.sprintName}
              </StyledLink>
            )}

            {info.row.original?.sprintDetail?.startDate &&
              info.row.original?.sprintDetail?.endDate && (
                <Text fontSize={'xxs'} color="primary" fontFamily="heading">
                  {moment(info.row.original?.sprintDetail?.startDate).format(
                    'DD MMM'
                  )}{' '}
                  -{' '}
                  {moment(info.row.original?.sprintDetail?.endDate).format(
                    'DD MMM'
                  )}
                </Text>
              )}

            <Stack mt={2} spacing={1}>
              {info.row.original?.sprintDetail?.sprintDuration && (
                <Text as="span" fontWeight={'medium'} color="text.primary">
                  Days :{' '}
                  <Text as="span" fontWeight="normal" color="text.secondary">
                    {info.row.original?.sprintDetail?.sprintDuration}
                  </Text>
                </Text>
              )}

              {info.row.original?.sprintDetail?.status && (
                <Text as="span" fontWeight={'medium'} color="text.primary">
                  Status :{' '}
                  <Text as="span" fontWeight="normal" color="text.secondary">
                    {info.row.original?.sprintDetail?.status}
                  </Text>
                </Text>
              )}

              {info.row.original?.sprintDetail?.overCommitmentRate && (
                <Flex align="center" gap={1}>
                  <Text as="span" fontWeight={'medium'} color="text.primary">
                    Overcommitment :{' '}
                    <Text as="span" fontWeight="normal" color="text.secondary">
                      {Number(
                        info.row.original?.sprintDetail?.overCommitmentRate
                      ) + '%' || 0}
                    </Text>
                  </Text>
                  <AppTooltip
                    trigger="hover"
                    content={
                      <MetricTooltipContent
                        description="How much of the planned work was not finished by the end of the sprint."
                        example="Planned 30 tasks -> Completed 20 -> 33.33% over-commitment"
                      />
                    }
                  >
                    <Box>
                      <InfoIcon width={14} height={14} />
                    </Box>
                  </AppTooltip>
                </Flex>
              )}

              {info.row.original?.sprintDetail?.scopeCreepRate && (
                <Flex align="center" gap={1}>
                  <Text as="span" fontWeight={'medium'} color="text.primary">
                    Scope Creep :{' '}
                    <Text as="span" fontWeight="normal" color="text.secondary">
                      {Number(info.row.original?.sprintDetail?.scopeCreepRate) +
                        '%' || 0}
                    </Text>
                  </Text>
                  <AppTooltip
                    trigger="hover"
                    content={
                      <MetricTooltipContent
                        description="How much extra work was added after the sprint started compared to what was originally planned."
                        example="Planned 30 tasks -> Added 15 later -> 50% scope creep"
                      />
                    }
                  >
                    <Box>
                      <InfoIcon width={14} height={14} />
                    </Box>
                  </AppTooltip>
                </Flex>
              )}

              {info.row.original?.sprintDetail?.blockedIssues?.length > 0 && (
                <Text as="span" fontWeight={'medium'} color="text.primary">
                  Blockers :{' '}
                  <Text
                    as="span"
                    fontWeight="normal"
                    color="text.secondary"
                    onClick={() => {
                      setBlockedIssues(
                        info.row.original?.sprintDetail?.blockedIssues
                      );
                      slideDrawer.onOpen();
                    }}
                    textDecoration={'underline'}
                    cursor={'pointer'}
                  >
                    {Number(
                      info.row.original?.sprintDetail?.blockedIssues?.length ||
                        0
                    )}
                  </Text>
                </Text>
              )}
            </Stack>
          </Flex>
        ),

        header: 'Sprint Name',
        size: 200,
      }),
      columnHelper.accessor('sprintPlanning', {
        cell: (info) => (
          <>
            <Flex
              direction="row"
              width="full"
              justify="space-between"
              align="center"
            >
              <Flex height="160px" width="240px" direction="column">
                <SprintBarChart
                  data={info.row.original?.sprintPlanning?.tasks?.map(
                    (item: any, idx: number) => ({
                      ...item,
                      value:
                        item.value === 'null'
                          ? 0
                          : item.value === null
                          ? 0
                          : parseFloat(item.value),
                      fill: fillColors[idx],
                    })
                  )}
                />
              </Flex>
              <Flex
                height="140px"
                width="140px"
                direction="column"
                align="center"
              >
                <DonutChart
                  percentage={
                    info.row.original?.sprintPlanning?.planningAccuracy
                  }
                />
                <Flex align="center" gap={1}>
                  <Text
                    fontSize="xs"
                    fontFamily="heading"
                    color="text.secondary"
                  >
                    Planning Accuracy
                  </Text>
                  <AppTooltip
                    trigger="hover"
                    content={
                      <MetricTooltipContent
                        description="How much of the planned work at the start of the sprint was completed."
                        example="Planned 30 tasks -> Completed 24 -> 80% accuracy"
                      />
                    }
                  >
                    <Box>
                      <InfoIcon width={14} height={14} />
                    </Box>
                  </AppTooltip>
                </Flex>
              </Flex>
            </Flex>
          </>
        ),

        header: 'Sprint Planning',
        size: 200,
        enableSorting: false,
      }),

      columnHelper.accessor('sprintProgress', {
        cell: (info) => (
          <>
            <Flex overflowY="auto" h={150}>
              <Flex
                height={
                  16.67 *
                    (info.row.original?.sprintProgress?.tasks?.filter(
                      (entry: any) =>
                        entry?.value !== 'null' &&
                        entry?.value !== null &&
                        parseFloat(entry?.value) !== 0
                    )?.length > 2
                      ? info.row.original?.sprintProgress?.tasks?.filter(
                          (entry: any) =>
                            entry?.value !== 'null' &&
                            entry?.value !== null &&
                            parseFloat(entry?.value) !== 0
                        )?.length
                      : 3) +
                  66.67
                }
                width="300px"
                direction="column"
              >
                <VerticalBarChartShared
                  XLabel="Count"
                  YLabel="Status"
                  data={
                    info.row.original?.sprintProgress?.tasks
                      ?.map((item: any, idx: number) => ({
                        ...item,
                        name: item.status || '',
                        value:
                          item.value === 'null'
                            ? 0
                            : item.value === null
                            ? 0
                            : parseFloat(item.value),
                        fill: item.color || '',
                      }))
                      ?.filter((entry: any) => entry?.value !== 0) || []
                  }
                />
              </Flex>
            </Flex>
          </>
        ),
        header: 'Current Status Dist',
        size: 300,
        enableSorting: false,
      }),
      columnHelper.accessor('velocity', {
        cell: (info) => (
          <>
            <Flex
              direction="column"
              width="full"
              justify={'center'}
              align="flex-start"
            >
              <Flex width="160px" height="160px">
                <SprintAreaChart
                  data={info.row.original?.sprintVelocity?.chart?.map(
                    (item: any) => ({
                      ...item,
                      completed: item.completed
                        ? parseFloat(item.completed)
                        : 0,
                    })
                  )}
                  chart="completed"
                />
              </Flex>
            </Flex>
          </>
        ),
        header: () => (
          <Flex align="center" gap={1}>
            <Text as="span" mb={0.5}>
              Velocity
            </Text>
            <AppTooltip
              trigger="hover"
              content={
                <Text fontSize="sm" fontFamily="heading" color="text.primary">
                  Shows how much work was completed per day during the sprint.
                  Each point is the total completed on that calendar day —
                  either number of issues or story points, depending on whether
                  you view by story points.
                </Text>
              }
            >
              <Box as="span" display="inline-flex" alignItems="center">
                <InfoIcon width={14} height={14} />
              </Box>
            </AppTooltip>
          </Flex>
        ),
        meta: { disableHeaderTooltip: true },
        size: 120,
        enableSorting: false,
      }),

      columnHelper.accessor('sprintTaskDist', {
        cell: (info) => (
          <>
            <Flex overflowY="auto" h={150}>
              <Flex
                height={
                  16.67 *
                    (info.row.original?.sprintTaskDist?.tasksDistribution?.filter(
                      (entry: any) =>
                        entry?.value !== 'null' &&
                        entry?.value !== null &&
                        parseFloat(entry?.value) !== 0
                    )?.length > 2
                      ? info.row.original?.sprintTaskDist?.tasksDistribution?.filter(
                          (entry: any) =>
                            entry?.value !== 'null' &&
                            entry?.value !== null &&
                            parseFloat(entry?.value) !== 0
                        )?.length
                      : 3) +
                  66.67
                }
                width="300px"
                direction="column"
              >
                <VerticalBarChartShared
                  XLabel="Count"
                  YLabel="Task"
                  data={
                    info.row.original?.sprintTaskDist?.tasksDistribution
                      ?.map((item: any, idx: number) => ({
                        ...item,
                        name: item.status || '',
                        value:
                          item.value === 'null'
                            ? 0
                            : item.value === null
                            ? 0
                            : parseFloat(item.value),
                        fill: item.color || '',
                      }))
                      ?.filter((entry: any) => entry?.value !== 0) || []
                  }
                />
              </Flex>
            </Flex>
          </>
        ),
        header: 'Task Distribution',
        size: 300,
        enableSorting: false,
      }),
    ],
    [data?.reports]
  );

  const handlePageClick = useCallback(
    (page: number) => {
      setCurrentPage(page - 1);
    },
    [currentPage]
  );

  return (
    <>
      <DataGrid
        showLoader={loading}
        columns={columns}
        data={rows}
        maxH={maxH ? maxH : '550px'}
        sticky="firstCol"
      />
      <Box marginLeft="-20px" my={'16px'}>
        <Paginate
          currentPage={data?.currentPage + 1}
          totalPageCount={data?.totalPages}
          pageSize={20}
          onPageChange={(page) => handlePageClick(page as number)}
        />
      </Box>
    </>
  );
};

export default SprintTableShared;
